"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { getTour, routePath, type Tour, type TourStep } from "@/lib/tour/tours";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import { TourOverlay } from "./tour-overlay";

interface TourState {
  tourId: string;
  stepIndex: number;
}

interface TourContextValue {
  active: boolean;
  tour: Tour | null;
  step: TourStep | null;
  stepIndex: number;
  total: number;
  start: (tourId: string) => void;
  next: () => void;
  back: () => void;
  end: () => void;
}

/** sessionStorage: a tour survives hard reloads mid-demo but dies with the tab. */
const STORAGE_KEY = "spark-tour";

const TourContext = createContext<TourContextValue | null>(null);

function readStored(): TourState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TourState;
    const tour = getTour(parsed?.tourId);
    if (!tour || typeof parsed.stepIndex !== "number") return null;
    if (parsed.stepIndex < 0 || parsed.stepIndex >= tour.steps.length) return null;
    return { tourId: parsed.tourId, stepIndex: parsed.stepIndex };
  } catch {
    return null;
  }
}

export function TourProvider({
  staff,
  children,
}: {
  staff: boolean;
  children: ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { demoMode } = useClientSidebar();
  // Starts null on both server and client render, so no hydration mismatch;
  // a persisted tour is restored in the mount effect below.
  const [state, setState] = useState<TourState | null>(null);

  const update = useCallback((next: TourState | null) => {
    setState(next);
    try {
      if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable: the tour still works for this page lifetime
    }
  }, []);

  // Restore a persisted tour after a hard reload; re-push its route if needed.
  useEffect(() => {
    if (!staff) return;
    const stored = readStored();
    if (!stored) return;
    // Restoring from sessionStorage must happen post-hydration (a lazy
    // initializer would mismatch the server-rendered null state).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(stored);
    const route = getTour(stored.tourId)?.steps[stored.stepIndex]?.route;
    if (route && routePath(route) !== window.location.pathname) router.push(route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Demo mode off (or lost staff context) ends the tour.
  useEffect(() => {
    // update() also clears sessionStorage (external store), so this stays an
    // effect rather than a render-time adjustment.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (state && !demoMode) update(null);
  }, [state, demoMode, update]);

  const tour = state ? (getTour(state.tourId) ?? null) : null;
  const step = tour && state ? (tour.steps[state.stepIndex] ?? null) : null;
  const active = Boolean(staff && demoMode && tour && step);

  const start = useCallback(
    (tourId: string) => {
      if (!staff) return;
      const target = getTour(tourId);
      if (!target) return;
      update({ tourId, stepIndex: 0 });
      if (routePath(target.steps[0].route) !== pathname) router.push(target.steps[0].route);
    },
    [staff, pathname, router, update]
  );

  const end = useCallback(() => update(null), [update]);

  // Finish + return to the Guided Tours launcher — offered on the last step of
  // staff onboarding tours so a new hire can move on to the next one.
  const endToLauncher = useCallback(() => {
    update(null);
    router.push("/agency/tours");
  }, [update, router]);

  const go = useCallback(
    (delta: number) => {
      if (!state || !tour) return;
      const idx = state.stepIndex + delta;
      if (idx >= tour.steps.length) {
        update(null);
        return;
      }
      if (idx < 0) return;
      update({ tourId: state.tourId, stepIndex: idx });
      const target = tour.steps[idx];
      if (routePath(target.route) !== pathname) router.push(target.route);
    },
    [state, tour, pathname, router, update]
  );

  const next = useCallback(() => go(1), [go]);
  const back = useCallback(() => go(-1), [go]);

  // Keyboard navigation while the tour is active.
  useEffect(() => {
    if (!active) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") end();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, end, next, back]);

  return (
    <TourContext.Provider
      value={{
        active,
        tour,
        step,
        stepIndex: state?.stepIndex ?? 0,
        total: tour?.steps.length ?? 0,
        start,
        next,
        back,
        end,
      }}
    >
      {children}
      {active && tour && step && state && (
        <TourOverlay
          step={step}
          stepIndex={state.stepIndex}
          total={tour.steps.length}
          pathname={pathname}
          onNext={next}
          onBack={back}
          onEnd={end}
          onEndToLauncher={tour.category === "onboarding" ? endToLauncher : undefined}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return ctx;
}
